/**
 * Pure outdoor layout (+field): turns a field DungeonDef + its outdoor theme into the terrain
 * (signed distance to the walkable edge, heights, path / plaza weights) and every placement
 * (edge trees, edge lines, obstacle clumps, decor, town houses, authored prop hints, lights).
 * No three.js; outdoor/field.ts meshes it and the tests check it (walkable ground stays clear of
 * solid props, portals stay open, deterministic).
 *
 *  - The walkable ground (rooms + corridors) is flat at y = 0 (units, telegraphs, picking).
 *  - Beyond the edge the terrain rises into hills; a fence / hedge / bramble line marks the edge,
 *    a dense band of full trees stands behind it, a far ring of cheap instanced trees fills the
 *    distance so no void ever shows.
 *  - DungeonDef.obstacles (tree clumps, rocks) get trees / boulders standing on them.
 *  - Town: cottages ring the square, stalls and lanterns line it, a well marks the middle.
 */
import type { DungeonDef, PortalDef, PropHint, Vec2 } from '@keystone/sim';
import { fieldAreaAt } from '@keystone/sim';
import { fieldTerrainOf } from '@keystone/content';
import { type Poly, type Pt, bbox, centroid, distToSegment, pointInPoly } from '../dressing/geom2d';
import { hashString, seededRandom } from '../layout';
import type { RGB } from '../themes/types';
import type { BiomeLook, OutdoorTheme, ScatterItem } from './themes';
import { type EdgeFrame, edgeLift, mountainK } from './edge'; // +edge

/** 'ground': flat walk-over stones (flagstones), 'glow': unlit crystals (+valley). */
export type OutLayer = 'prop' | 'detail' | 'ground' | 'glow';

export interface OutPlacement {
  /** Proc id (outdoor/procs.ts, `name#variant`) or a theme kit model id ('kd_*', 'kn_*'). */
  id: string;
  pos: Pt;
  y: number;
  /** three.js rotation.y (local +z → sim direction, see yawToward). */
  yaw: number;
  scale: number;
  tint: RGB;
  layer: OutLayer;
  /** Footprint radius (yd, scaled). */
  r: number;
  /** Height (yd, scaled). */
  h: number;
  castShadow: boolean;
}

export type OutLightKind = 'lantern' | 'fire' | 'crystal' | 'forge' | 'rune' | 'monolith';

export interface OutLight {
  pos: Pt;
  /** Height above the ground (yd). */
  y: number;
  kind: OutLightKind;
}

export interface FarTree {
  pos: Pt;
  scale: number;
  yaw: number;
  pine: boolean;
  /** +zone: per-instance tint (the biome's far-tree tint). */
  tint?: RGB;
}

/** +zone: a biome's resolved look (the theme with the biome's overrides). */
export interface Look {
  id: string;
  ground: OutdoorTheme['ground'];
  edgeTrees: readonly ScatterItem[];
  edgeUnder: readonly ScatterItem[];
  obstacleTrees: readonly ScatterItem[];
  obstacleRocks: readonly ScatterItem[];
  rockChance: number;
  decor: readonly ScatterItem[];
  decorDensity: number;
  grassK: number;
  cliffK: number;
  farTint: RGB;
  /** +detail: walk-through nature props (bushes, boulders, logs) over the open ground, per 100 yd². */
  scatter: readonly ScatterItem[];
  scatterDensity: number;
  /** +detail: verge dressing along the roads (flowers, grass clumps, pebbles; bushes now and then). */
  verge: readonly ScatterItem[];
  /** +detail: share of the far ring that is round broadleaf trees (the rest conifers). */
  farRound: number;
  /** Hill height beyond the edge (else the theme's) and the distance (yd) it takes to rise (sharp = crags). */
  hills?: number;
  hillSharp?: number;
}

export interface OutPath {
  pts: Pt[];
  width: number;
  /** +zone: dirt only (side trails), no flagstones. */
  trail?: boolean;
}

/** A regular grid over the terrain extent (row-major, x fastest). */
export interface Grid {
  x0: number;
  y0: number;
  cell: number;
  nx: number;
  ny: number;
}

export interface FieldLayout {
  theme: OutdoorTheme;
  walk: Poly[];
  obstacles: Poly[];
  grid: Grid;
  /** Signed distance to the walkable edge (negative inside), per grid vertex. */
  sdf: Float32Array;
  /** Terrain height (0 on the walkable ground), per grid vertex. */
  height: Float32Array;
  /** Worn path weight 0..1, per grid vertex. */
  path: Float32Array;
  /** Town cobble weight 0..1, per grid vertex. */
  plaza: Float32Array;
  /** Shade under / near edge trees 0..1, per grid vertex. */
  shade: Float32Array;
  placements: OutPlacement[];
  far: FarTree[];
  paths: OutPath[];
  lights: OutLight[];
  /** Kept-clear spots (portals, the barrow mound, NPCs). */
  reserved: { pos: Pt; r: number }[];
  /** Map centre used as the path hub. */
  hub: Pt;
  /** +zone: the biome look at a point (the theme's own look on maps without areas). */
  look(x: number, y: number): Look;
  /** +zone: the terrain comes from content (heights inside the walkable ground too). */
  terrain: boolean;
  /** +zone: a point inside an obstacle (bucketed: cheap on big maps). */
  blocked(x: number, y: number): boolean;
}

/** +zone: the theme's look with a biome's overrides. */
export function resolveLook(theme: OutdoorTheme, id: string): Look {
  const b: BiomeLook = theme.biomes?.[id] ?? {};
  return {
    id,
    ground: { ...theme.ground, ...(b.ground ?? {}) },
    edgeTrees: b.edgeTrees ?? theme.edgeTrees,
    edgeUnder: b.edgeUnder ?? theme.edgeUnder,
    obstacleTrees: b.obstacleTrees ?? theme.obstacleTrees,
    obstacleRocks: b.obstacleRocks ?? theme.obstacleRocks,
    rockChance: b.rockChance ?? (theme.id === 'forest' ? 0.3 : 0.45),
    decor: b.decor ?? theme.decor,
    decorDensity: b.decorDensity ?? theme.decorDensity,
    grassK: b.grassK ?? 1,
    cliffK: b.cliffK ?? 1,
    farTint: b.farTint ?? [1, 1, 1],
    scatter: b.scatter ?? theme.scatter ?? [],
    scatterDensity: b.scatterDensity ?? theme.scatterDensity ?? 0,
    verge: b.verge ?? theme.verge ?? [],
    farRound: b.farRound ?? 0,
    ...(b.hills !== undefined ? { hills: b.hills } : {}),
    ...(b.hillSharp !== undefined ? { hillSharp: b.hillSharp } : {}),
  };
}

/** +zone: biome lookup on a coarse grid (area biome at the cell centre, coordinates clamped into the map). */
function biomeLookup(d: DungeonDef, theme: OutdoorTheme, x0: number, y0: number, w: number, h: number): (x: number, y: number) => Look {
  const base = resolveLook(theme, theme.id === 'meadow' ? 'meadow' : theme.id);
  const areas = d.field?.areas;
  if (!areas?.length || !theme.biomes) return () => base;
  const looks = new Map<string, Look>();
  const lookFor = (id: string): Look => {
    let l = looks.get(id);
    if (!l) looks.set(id, (l = resolveLook(theme, id)));
    return l;
  };
  const C = 4;
  const nx = Math.ceil(w / C) + 1;
  const ny = Math.ceil(h / C) + 1;
  const b = d.bounds;
  const cells: Look[] = new Array(nx * ny);
  for (let j = 0; j < ny; j++)
    for (let i = 0; i < nx; i++) {
      // +zone v2: the biome map is domain-warped, so the borders between areas wander instead of
      // following the areas' polygon edges (no straight seams in the ground colour).
      const gx = x0 + i * C;
      const gy = y0 + j * C;
      const wx = gx + BIOME_WARP * (vnoise(gx * 0.017 + 3.3, gy * 0.017 - 1.1) - 0.5) * 2;
      const wy = gy + BIOME_WARP * (vnoise(gx * 0.017 - 7.7, gy * 0.017 + 5.2) - 0.5) * 2;
      const p = { x: Math.min(b.x + b.w - 3, Math.max(b.x + 3, wx)), y: Math.min(b.y + b.h - 3, Math.max(b.y + 3, wy)) };
      const a = fieldAreaAt(d.field, p);
      const id = a?.biome ?? (a?.region ? areas.find((r) => r.id === a.region)?.biome : undefined) ?? 'meadow';
      cells[j * nx + i] = lookFor(id);
    }
  return (x, y) => {
    const i = Math.min(nx - 1, Math.max(0, Math.round((x - x0) / C)));
    const j = Math.min(ny - 1, Math.max(0, Math.round((y - y0) / C)));
    return cells[j * nx + i]!;
  };
}

/** +zone v2: how far (yd) the biome map's borders wander from the area polygons. */
const BIOME_WARP = 20;

/** Terrain margin beyond the map bounds (yd). */
export const FIELD_MARGIN = 64;
const CELL = 1.25;

/** three.js rotation.y turning local +z toward the sim direction (dx, dy). */
export const yawToward = (dx: number, dy: number): number => Math.atan2(dx, -dy);

// ---------------------------------------------------------------------------------------------
// Noise / helpers
// ---------------------------------------------------------------------------------------------

function hash2(x: number, y: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
export function vnoise(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi);
  const b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1);
  const e = hash2(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + e) * u * v;
}
export const smooth = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

function pick<T extends { w: number }>(items: readonly T[], rnd: () => number): T {
  let total = 0;
  for (const it of items) total += it.w;
  let r = rnd() * total;
  for (const it of items) {
    r -= it.w;
    if (r <= 0) return it;
  }
  return items[items.length - 1]!;
}

const lerpTint = (t: readonly [RGB, RGB] | undefined, k: number): RGB =>
  t ? [t[0][0] + (t[1][0] - t[0][0]) * k, t[0][1] + (t[1][1] - t[0][1]) * k, t[0][2] + (t[1][2] - t[0][2]) * k] : [1, 1, 1];

/** Bilinear sample of a grid field at sim (x, y). */
export function sampleGrid(g: Grid, f: Float32Array, x: number, y: number): number {
  const gx = Math.min(g.nx - 1.001, Math.max(0, (x - g.x0) / g.cell));
  const gy = Math.min(g.ny - 1.001, Math.max(0, (y - g.y0) / g.cell));
  const ix = Math.floor(gx);
  const iy = Math.floor(gy);
  const fx = gx - ix;
  const fy = gy - iy;
  const i = iy * g.nx + ix;
  const a = f[i]!;
  const b = f[i + 1]!;
  const c = f[i + g.nx]!;
  const d = f[i + g.nx + 1]!;
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
}

/** Portals with their mound / frame footprint (the barrow mound sits behind its mouth). */
export function portalFootprint(p: PortalDef): { pos: Pt; r: number } {
  if (p.kind === 'dungeon') return { pos: { x: p.pos.x - Math.cos(p.facing) * 5.5, y: p.pos.y - Math.sin(p.facing) * 5.5 }, r: 10.5 };
  return { pos: p.pos, r: p.radius + 3.6 };
}

// ---------------------------------------------------------------------------------------------
// Authored prop hints (DungeonDef.props) → placements
// ---------------------------------------------------------------------------------------------

/** One authored hint kind → procs (and an optional light). `theme` picks tree species. */
interface HintRule {
  ids: (t: OutdoorTheme) => readonly string[];
  r: number;
  h: number;
  detail?: boolean;
  light?: { kind: OutLightKind; dx: number; dz: number; y: number };
  /** Several pieces scattered around the hint (clumps). */
  clump?: number;
  /** Not drawn from the hint (portal frames come from PortalDefs; obstacles fill themselves). */
  skip?: boolean;
  /** Fill the obstacle under the hint with the theme's trees / rocks instead. */
  obstacleFill?: boolean;
  /** Flat scatter: `scale` is a radius (yd) filled with these detail pieces. */
  area?: 'flowers' | 'mushrooms' | 'reeds' | 'rows';
  /** A row of segments along the hint's local x: `scale` × 2 segments of `segment` yd. */
  row?: number;
  /** Base model scale (kit models authored at another size). */
  base?: number;
}

const treeIds = (t: OutdoorTheme): readonly string[] => (t.id === 'forest' ? ['tree_oak', 'pine', 'pine_tall'] : t.id === 'town' ? ['tree_round', 'tree_blossom'] : ['tree_round', 'tree_small', 'tree_blossom']);
const one = (...ids: string[]) => (): readonly string[] => ids;

/** Prop hint kinds the outdoor builder supports (aliases resolve to these, see hintRule). */
export const HINT_RULES: Record<string, HintRule> = {
  tree: { ids: treeIds, r: 1.4, h: 6.5 },
  trees: { ids: treeIds, r: 1.4, h: 6.5, clump: 4 },
  tree_clump: { ids: treeIds, r: 1.4, h: 6.5, obstacleFill: true, clump: 4 },
  oak: { ids: one('tree_oak', 'tree_oak_big'), r: 1.8, h: 8 },
  pine: { ids: one('pine', 'pine_tall'), r: 1.4, h: 8 },
  dead_tree: { ids: one('kh_tree_dead_medium', 'kh_tree_dead_small'), r: 1.0, h: 5, base: 2.2 },
  blossom: { ids: one('tree_blossom'), r: 1.4, h: 5.6 },
  bush: { ids: (t) => (t.id === 'forest' ? ['bush_dark'] : ['bush']), r: 0.9, h: 1.3 },
  bramble: { ids: one('bramble'), r: 1.0, h: 1.2 },
  flowers: { ids: one('flowers', 'flowers_red', 'flowers_blue'), r: 0.8, h: 0.4, detail: true },
  flower_patch: { ids: one('flowers', 'flowers_red', 'flowers_blue'), r: 0.8, h: 0.4, detail: true, area: 'flowers' },
  mushroom_ring: { ids: one('mushrooms'), r: 0.4, h: 0.3, detail: true, area: 'mushrooms' },
  rock: { ids: one('rock_big', 'boulder'), r: 1.4, h: 1.4 },
  rocks: { ids: one('rock', 'rock_small', 'rock_big'), r: 1.0, h: 1.0, clump: 3 },
  boulder: { ids: one('boulder'), r: 1.4, h: 1.6 },
  cliff: { ids: one('cliff_rock'), r: 2.4, h: 2.6, base: 1.4 },
  standing_stone: { ids: one('standing_stone'), r: 0.6, h: 3.6 },
  stump: { ids: one('stump'), r: 0.5, h: 0.6 },
  log: { ids: one('log'), r: 1.2, h: 0.6 },
  mushrooms: { ids: one('mushrooms'), r: 0.4, h: 0.3, detail: true },
  fence: { ids: one('fence'), r: 0.3, h: 1.25, row: 3 },
  palisade: { ids: one('palisade'), r: 0.3, h: 3, row: 3 },
  hedge: { ids: one('hedge'), r: 0.8, h: 1.4 },
  cottage: { ids: one('cottage_red', 'cottage_slate', 'cottage_thatch'), r: 3.2, h: 7 },
  stall: { ids: one('stall_red', 'stall_blue'), r: 1.6, h: 3 },
  well: { ids: one('well'), r: 1.3, h: 3.7 },
  lantern: { ids: one('lantern_post'), r: 0.3, h: 3.1, light: { kind: 'lantern', dx: 0.75, dz: 0, y: 2.45 } },
  banner: { ids: one('banner_red', 'banner_blue'), r: 0.3, h: 5 },
  bandit_banner: { ids: one('banner_bandit'), r: 0.3, h: 5 },
  tent: { ids: one('tent'), r: 1.6, h: 2.3 },
  campfire: { ids: one('campfire'), r: 0.8, h: 0.4, light: { kind: 'fire', dx: 0, dz: 0, y: 0.15 } },
  crates: { ids: one('crates'), r: 0.9, h: 1.6 },
  barrel: { ids: one('barrel'), r: 0.5, h: 1.1 },
  hay_bale: { ids: one('hay_bale'), r: 0.8, h: 1.2 },
  signpost: { ids: one('signpost'), r: 0.3, h: 2.6 },
  notice_board: { ids: one('notice_board'), r: 1.2, h: 3 },
  anvil: { ids: one('anvil'), r: 1.0, h: 1.2, light: { kind: 'forge', dx: -1.7, dz: -0.2, y: 1.0 } },
  forge: { ids: one('forge'), r: 1.6, h: 4, light: { kind: 'forge', dx: 0, dz: 0.1, y: 1.05 } },
  cart: { ids: one('cart'), r: 1.2, h: 1.6 },
  bench: { ids: one('bench'), r: 0.6, h: 1 },
  windmill: { ids: one('windmill'), r: 2.6, h: 12 },
  watchtower: { ids: one('watchtower'), r: 2.0, h: 8 },
  ruin_pillar: { ids: one('ruin_pillar'), r: 0.8, h: 3 },
  ruin: { ids: one('ruin_wall'), r: 1.2, h: 2.6 },
  crystals: { ids: one('crystals'), r: 0.7, h: 2.0, light: { kind: 'crystal', dx: 0, dz: 0, y: 1.0 } },
  // +valley: pale ruins on the high ground (outdoor/valley.ts). Big footprints keep the trees off.
  ruin_arch: { ids: one('ruin_arch'), r: 4.5, h: 9 },
  ruin_colonnade: { ids: one('ruin_colonnade'), r: 5.5, h: 7 },
  ruin_tower: { ids: one('ruin_tower'), r: 5, h: 16 },
  ruin_wall_pale: { ids: one('ruin_wall_pale'), r: 3.5, h: 3.5 },
  roots: { ids: one('roots'), r: 0.8, h: 2 },
  // +zone: the lake, the mine, the grotto; arch piers block but draw nothing (the arch is its own prop).
  reeds: { ids: one('reeds'), r: 0.5, h: 1.4, detail: true, area: 'reeds' },
  dock: { ids: one('dock'), r: 1.2, h: 1.4 },
  mine_mouth: { ids: one('mine_mouth'), r: 3, h: 6 },
  cave_mouth: { ids: one('cave_mouth'), r: 3, h: 5 },
  waterfall: { ids: one(), r: 1, h: 1, skip: true },
  pier: { ids: one(), r: 0.9, h: 1 },
  water: { ids: one(), r: 1, h: 0 },
  portal_frame: { ids: one(), r: 1, h: 1, skip: true },
  dirt_path: { ids: one(), r: 1, h: 0, skip: true },
  trail: { ids: one(), r: 1, h: 0, skip: true },
  // +zone: Tallowdeep Mine's dressing.
  ore_cart: { ids: one('ore_cart'), r: 1.0, h: 1.4 },
  mine_rails: { ids: one('mine_rails'), r: 0.8, h: 0.1, detail: true },
  spoil_heap: { ids: one('spoil_heap'), r: 2.4, h: 2 },
  mine_beams: { ids: one('mine_beams'), r: 1.2, h: 3 },
  // +detail: the quiet country's points of interest and the farm's dressing (content mossvaleZone.ts).
  eldertree: { ids: one('eldertree'), r: 4.4, h: 40 },
  menhir: { ids: one('mossrock_menhir'), r: 0.9, h: 4.2 },
  statue_plinth: { ids: one('statue_plinth'), r: 2.4, h: 4.5 },
  statue_torso: { ids: one('statue_torso'), r: 2, h: 2.2 },
  statue_head: { ids: one('statue_head'), r: 2, h: 3 },
  statue_hand: { ids: one('statue_hand'), r: 1.5, h: 2.2 },
  statue_broken: { ids: one('statue_broken'), r: 1.3, h: 2.6 },
  hermit_hut: { ids: one('hut'), r: 2.8, h: 5.5 },
  wayside_shrine: { ids: one('shrine'), r: 1.3, h: 3.1, light: { kind: 'lantern', dx: 0.25, dz: 0.4, y: 1.55 } },
  tent_torn: { ids: one('tent_torn'), r: 1.8, h: 1.8 },
  cart_wreck: { ids: one('cart_wreck'), r: 1.6, h: 1.6 },
  campfire_cold: { ids: one('campfire'), r: 0.8, h: 0.4 },
  bedroll: { ids: one('bedroll'), r: 0.8, h: 0.4 },
  woodpile: { ids: one('woodpile'), r: 1.1, h: 1.2 },
  drying_rack: { ids: one('drying_rack'), r: 1.2, h: 2 },
  washing_line: { ids: one('washing_line'), r: 0.4, h: 2.1 }, // +spawn
  rowboat: { ids: one('rowboat'), r: 1.2, h: 0.8 },
  scarecrow: { ids: one('scarecrow'), r: 0.5, h: 2.8 },
  wildflowers: { ids: one('flowers_daisy', 'flowers_poppy', 'flowers_butter', 'flowers_lupin', 'flowers_mixed', 'bluebells'), r: 0.8, h: 0.5, detail: true, area: 'flowers' },
  crop_rows: { ids: one('barley_row'), r: 1.5, h: 1, detail: true, area: 'rows' },
};

/** Alias table: authored kind (lowercased, normalised) → rule key. */
const HINT_ALIASES: [RegExp, string][] = [
  [/^(town_gate|gate_arch|portal_stones|portal_ring|barrow_arch|barrow_mound|barrow_mouth)$/, 'portal_frame'],
  [/^(dirt_path|road|path|tower_road)$/, 'dirt_path'],
  [/^(tree_?clump|grove|copse|trees?_group)/, 'tree_clump'],
  [/^trees$/, 'trees'],
  [/dead_?tree/, 'dead_tree'],
  [/oak/, 'oak'],
  [/pine|fir|spruce|conifer/, 'pine'],
  [/blossom|cherry/, 'blossom'],
  [/tree/, 'tree'],
  [/bramble|thorn|briar/, 'bramble'],
  [/bush|shrub/, 'bush'],
  [/flower_?(patch|bed|field)/, 'flower_patch'],
  [/mushroom_?ring|fairy_ring/, 'mushroom_ring'],
  [/flower/, 'flowers'],
  [/boulder/, 'boulder'],
  [/cliff/, 'cliff'],
  [/standing|menhir|monolith|obelisk/, 'standing_stone'],
  [/rocks|rubble|stones$/, 'rocks'],
  [/rock|stone/, 'rock'],
  [/stump/, 'stump'],
  [/log/, 'log'],
  [/mushroom|shroom/, 'mushrooms'],
  [/palisade|stockade/, 'palisade'],
  [/fence/, 'fence'],
  [/hedge/, 'hedge'],
  [/windmill|mill/, 'windmill'],
  [/watch_?tower|lookout|tower/, 'watchtower'],
  [/cottage|house|home|hut|inn|tavern|building/, 'cottage'],
  [/stall|market|vendor_stand/, 'stall'],
  [/well/, 'well'],
  [/lantern|lamp|torch_post|streetlight/, 'lantern'],
  [/bandit_?(banner|flag)/, 'bandit_banner'],
  [/banner|flag/, 'banner'],
  [/tent/, 'tent'],
  [/camp_?fire|fire_?pit|bonfire|fire/, 'campfire'],
  [/crate|box/, 'crates'],
  [/barrel|keg/, 'barrel'],
  [/hay/, 'hay_bale'],
  [/sign/, 'signpost'],
  [/notice|board/, 'notice_board'],
  [/forge|smithy/, 'forge'],
  [/anvil/, 'anvil'],
  [/cart|wagon/, 'cart'],
  [/bench|seat/, 'bench'],
  [/ruin_?(pillar|column)|pillar|column/, 'ruin_pillar'],
  [/ruin|wall/, 'ruin'],
  [/crystal/, 'crystals'],
  [/root/, 'roots'],
];

/** The rule for an authored hint kind (exact key first, then aliases), or null. */
export function hintRule(kind: string): HintRule | null {
  const k = kind.toLowerCase().replace(/[\s-]+/g, '_');
  // The Tower on the horizon is a backdrop (outdoor/tower.ts), not a placed prop.
  if (k === 'tower_landmark') return null;
  if (HINT_RULES[k]) return HINT_RULES[k]!;
  for (const [re, key] of HINT_ALIASES) if (re.test(k)) return HINT_RULES[key]!;
  return null;
}

// ---------------------------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------------------------

export function layoutField(d: DungeonDef, theme: OutdoorTheme): FieldLayout {
  const rnd = seededRandom(hashString(`field:${d.id}`));
  const walk: Poly[] = [...d.rooms.map((r) => r.shape.points.map((p) => ({ x: p.x, y: p.y }))), ...d.corridors.map((c) => c.shape.points.map((p) => ({ x: p.x, y: p.y })))];
  const obstacles: Poly[] = (d.obstacles ?? []).map((o) => o.points.map((p) => ({ x: p.x, y: p.y })));
  // +zone: big maps: obstacles bucketed by bounding box (point tests look at one bucket).
  const obsBox = obstacles.map((o) => bbox(o));
  const obsHash = new SpatialHash<number>(16);
  obsBox.forEach((bb, i) => obsHash.addBox(bb.x0, bb.y0, bb.x1, bb.y1, i));
  const inObstacle = (p: Pt): Poly | undefined => {
    for (const i of obsHash.at(p.x, p.y)) {
      const bb = obsBox[i]!;
      if (p.x < bb.x0 || p.x > bb.x1 || p.y < bb.y0 || p.y > bb.y1) continue;
      if (pointInPoly(p, obstacles[i]!)) return obstacles[i];
    }
    return undefined;
  };
  const portals = d.field?.portals ?? [];
  const npcs = d.field?.npcs ?? [];

  // ---- grid + signed distance --------------------------------------------------------
  const b = d.bounds;
  const x0 = b.x - FIELD_MARGIN;
  const y0 = b.y - FIELD_MARGIN;
  const nx = Math.ceil((b.w + FIELD_MARGIN * 2) / CELL) + 1;
  const ny = Math.ceil((b.h + FIELD_MARGIN * 2) / CELL) + 1;
  const grid: Grid = { x0, y0, cell: CELL, nx, ny };
  const N = nx * ny;
  const sdf = new Float32Array(N);
  const segs: [Pt, Pt][] = [];
  for (const p of walk) for (let i = 0, j = p.length - 1; i < p.length; j = i++) segs.push([p[j]!, p[i]!]);
  // Coarse buckets of segments for the distance query.
  const BK = 12;
  const buckets = new Map<number, number[]>();
  segs.forEach(([a, c], i) => {
    for (let bx = Math.floor((Math.min(a.x, c.x) - x0) / BK); bx <= Math.floor((Math.max(a.x, c.x) - x0) / BK); bx++)
      for (let by = Math.floor((Math.min(a.y, c.y) - y0) / BK); by <= Math.floor((Math.max(a.y, c.y) - y0) / BK); by++) {
        const k = bx * 4096 + by;
        const l = buckets.get(k);
        if (l) l.push(i);
        else buckets.set(k, [i]);
      }
  });
  const edgeDist = (p: Pt): number => {
    const bx = Math.floor((p.x - x0) / BK);
    const by = Math.floor((p.y - y0) / BK);
    let best = Infinity;
    for (let r = 0; r < 12; r++) {
      for (let ix = bx - r; ix <= bx + r; ix++)
        for (let iy = by - r; iy <= by + r; iy++) {
          if (Math.max(Math.abs(ix - bx), Math.abs(iy - by)) !== r) continue;
          const l = buckets.get(ix * 4096 + iy);
          if (!l) continue;
          for (const i of l) {
            const s = segs[i]!;
            const dd = distToSegment(p, s[0], s[1]);
            if (dd < best) best = dd;
          }
        }
      // Anything in ring r+1 is at least r·BK away.
      if (best <= r * BK) break;
    }
    return Math.min(best, 999);
  };
  const inWalk = (p: Pt): boolean => walk.some((poly) => pointInPoly(p, poly));
  const pt = { x: 0, y: 0 };
  // +zone: inside / outside per row by scanline crossings (any number of walkable polygons).
  const inside = new Uint8Array(N);
  for (let j = 0; j < ny; j++) {
    const y = y0 + j * CELL;
    for (const poly of walk) {
      const xs: number[] = [];
      for (let i = 0, k = poly.length - 1; i < poly.length; k = i++) {
        const a = poly[k]!;
        const c = poly[i]!;
        if (a.y > y !== c.y > y) xs.push(a.x + ((y - a.y) / (c.y - a.y)) * (c.x - a.x));
      }
      xs.sort((u, v) => u - v);
      for (let q = 0; q + 1 < xs.length; q += 2) {
        const i0 = Math.max(0, Math.ceil((xs[q]! - x0) / CELL));
        const i1 = Math.min(nx - 1, Math.floor((xs[q + 1]! - x0) / CELL));
        for (let i = i0; i <= i1; i++) inside[j * nx + i] = 1;
      }
    }
  }
  // +zone: a coarse pass first: each segment bucket's distance to the nearest edge; cells whose
  // bucket is far from every edge take that (big open maps: no ring search deep inside or far out).
  const BX = Math.ceil((nx * CELL) / BK) + 1;
  const BY = Math.ceil((ny * CELL) / BK) + 1;
  const coarse = new Float32Array(BX * BY);
  for (let by = 0; by < BY; by++)
    for (let bx = 0; bx < BX; bx++) coarse[by * BX + bx] = edgeDist({ x: x0 + (bx + 0.5) * BK, y: y0 + (by + 0.5) * BK });
  const SDF_EXACT = 48;
  for (let j = 0; j < ny; j++)
    for (let i = 0; i < nx; i++) {
      pt.x = x0 + i * CELL;
      pt.y = y0 + j * CELL;
      // +edge: bilinear between bucket centres (a smooth distance far from the edge: mountain slopes).
      const fx = Math.min(BX - 1.001, Math.max(0, (pt.x - x0) / BK - 0.5));
      const fy = Math.min(BY - 1.001, Math.max(0, (pt.y - y0) / BK - 0.5));
      const bi = Math.floor(fx);
      const bj = Math.floor(fy);
      const tx = fx - bi;
      const ty = fy - bj;
      const c00 = coarse[bj * BX + bi]!;
      const c10 = coarse[bj * BX + Math.min(BX - 1, bi + 1)]!;
      const c01 = coarse[Math.min(BY - 1, bj + 1) * BX + bi]!;
      const c11 = coarse[Math.min(BY - 1, bj + 1) * BX + Math.min(BX - 1, bi + 1)]!;
      const cd = c00 + (c10 - c00) * tx + (c01 - c00) * ty + (c00 - c10 - c01 + c11) * tx * ty;
      const e = cd - BK * 0.75 > SDF_EXACT ? cd : edgeDist(pt);
      const k = j * nx + i;
      sdf[k] = inside[k] ? -e : e;
    }
  const distAt = (x: number, y: number): number => sampleGrid(grid, sdf, x, y);

  // ---- heights -----------------------------------------------------------------------
  const height = new Float32Array(N);
  const look = biomeLookup(d, theme, x0, y0, nx * CELL, ny * CELL);
  // +zone: content terrain (hills, the plateau, the lake basin) under everything; the edge hills on top.
  const terrain = fieldTerrainOf(d.id);
  // +edge: on a zone map (content terrain + biomes) the edge is mountains and forested hills (edge.ts).
  const towerHint0 = (d.props ?? []).find((h) => h.kind === 'tower_landmark');
  const edgeFrame: EdgeFrame | null =
    terrain && theme.biomes
      ? { cx: b.x + b.w / 2, cy: b.y + b.h / 2, towerAng: towerHint0 ? Math.atan2(towerHint0.pos.y - (b.y + b.h / 2), towerHint0.pos.x - (b.x + b.w / 2)) : null }
      : null;
  for (let j = 0; j < ny; j++)
    for (let i = 0; i < nx; i++) {
      const k = j * nx + i;
      const e = sdf[k]!;
      const x = x0 + i * CELL;
      const y = y0 + j * CELL;
      const base = terrain ? terrain.heightAt(x, y) : 0;
      if (edgeFrame) {
        // Bumps keep the slopes from reading as one smooth shell.
        const bumps = (vnoise(x * 0.16 + 11, y * 0.16) - 0.5) * 1.4 * smooth(2, 14, e);
        height[k] = base + edgeLift(edgeFrame, x, y, e) + (e > 0 ? bumps : 0);
        continue;
      }
      if (e <= 0.6) {
        height[k] = base;
        continue;
      }
      const rise = smooth(1.2, 30, e);
      const roll = 0.62 + 0.38 * vnoise(x * 0.045 + 3, y * 0.045 - 7);
      const bumps = (vnoise(x * 0.16 + 11, y * 0.16) - 0.5) * 1.2 * smooth(2, 14, e);
      // +zone: a rocky biome's hills rise steep and high (crags); others roll.
      const lk = look(x, y);
      const rise2 = lk.hillSharp ? smooth(1.0, lk.hillSharp, e) : rise;
      height[k] = base + (lk.hills ?? theme.hills) * rise2 * roll + bumps + 0.01;
    }

  // ---- hub, reserved spots -----------------------------------------------------------
  const biggest = walk.reduce((a, p) => (polyArea(p) > polyArea(a) ? p : a), walk[0] ?? []);
  const hub = biggest.length ? centroid(biggest) : { x: b.x + b.w / 2, y: b.y + b.h / 2 };
  const reserved: { pos: Pt; r: number }[] = [];
  for (const p of portals) reserved.push(portalFootprint(p));
  for (const n of npcs) reserved.push({ pos: n.pos, r: 2.4 });
  const isReserved = (p: Pt, pad = 0): boolean => reserved.some((r) => Math.hypot(r.pos.x - p.x, r.pos.y - p.y) < r.r + pad);

  // ---- paths (entrance / portals → hub) ------------------------------------------------
  const paths: OutPath[] = [];
  const roadHints = (d.props ?? []).filter((h) => hintRule(h.kind) === HINT_RULES['dirt_path'] || hintRule(h.kind) === HINT_RULES['trail']);
  if (roadHints.length) {
    // Authored roads: each decal is a ~4 yd segment along its facing.
    for (const h of roadHints) {
      const f = h.facing ?? 0;
      const fx = Math.cos(f);
      const fy = Math.sin(f);
      paths.push({ pts: [{ x: h.pos.x - fx * 1.2, y: h.pos.y - fy * 1.2 }, { x: h.pos.x + fx * 5.2, y: h.pos.y + fy * 5.2 }], width: Math.max(2, (h.scale ?? 3) * 1.1), ...(hintRule(h.kind) === HINT_RULES['trail'] ? { trail: true } : {}) });
    }
  } else if (theme.pathWidth > 0) {
    const ends: Pt[] = [d.entrance, ...portals.map((p) => p.pos)];
    for (const e of ends) {
      if (Math.hypot(e.x - hub.x, e.y - hub.y) < 4) continue;
      paths.push({ pts: wigglePath(e, hub, rnd), width: theme.pathWidth });
    }
  }
  const pathW = new Float32Array(N);
  if (paths.length) {
    // +zone: segments bucketed (a cell only looks at the segments within reach of it).
    const reach = Math.max(theme.pathWidth, ...paths.map((pa) => pa.width)) * 0.6 + 1;
    const segHash = new SpatialHash<[Pt, Pt, number]>(8);
    for (const pa of paths)
      for (let s = 0; s < pa.pts.length - 1; s++) {
        const a = pa.pts[s]!;
        const c = pa.pts[s + 1]!;
        segHash.addBox(Math.min(a.x, c.x) - reach, Math.min(a.y, c.y) - reach, Math.max(a.x, c.x) + reach, Math.max(a.y, c.y) + reach, [a, c, pa.width]);
      }
    for (let j = 0; j < ny; j++)
      for (let i = 0; i < nx; i++) {
        const k = j * nx + i;
        if (sdf[k]! > 8) continue;
        pt.x = x0 + i * CELL;
        pt.y = y0 + j * CELL;
        let best = Infinity;
        let w = theme.pathWidth;
        for (const sg of segHash.at(pt.x, pt.y)) {
          const dd = distToSegment(pt, sg[0], sg[1]);
          if (dd < best) {
            best = dd;
            w = sg[2];
          }
        }
        const edge = (vnoise(pt.x * 0.6, pt.y * 0.6) - 0.5) * 0.7;
        pathW[k] = 1 - smooth(w * 0.32 + edge, w * 0.55 + edge, best);
      }
  }

  // ---- vista notch (+valley): edge dressing thins toward the Tower, seen from the entrance / hub ----
  const towerHint = (d.props ?? []).find((h) => h.kind === 'tower_landmark');
  const towerDir = towerHint ? Math.atan2(towerHint.pos.y - hub.y, towerHint.pos.x - hub.x) : 0;
  const vistaFrom: Pt[] = [d.entrance, hub];
  const inVista = (p: Pt): boolean => {
    const w = theme.vistaNotch;
    if (!w || !towerHint) return false;
    return vistaFrom.some((o) => {
      let da = Math.atan2(p.y - o.y, p.x - o.x) - towerDir;
      da = Math.atan2(Math.sin(da), Math.cos(da));
      return Math.abs(da) < w;
    });
  };

  // ---- town plaza (cobbles inside the square, a grass verge at the edge) --------------
  const plaza = new Float32Array(N);
  // +zone: the town square's cobbles (a safe 'town' area on a zone map).
  for (const a of d.field?.areas ?? []) {
    if (!a.safe || a.biome !== 'town') continue;
    const c = centroid(a.shape.points);
    const rad = a.shape.points.reduce((m, q) => Math.max(m, Math.hypot(q.x - c.x, q.y - c.y)), 0) * 0.68;
    for (let k = 0; k < N; k++) {
      const x = x0 + (k % nx) * CELL;
      const y = y0 + Math.floor(k / nx) * CELL;
      const dd = Math.hypot(x - c.x, y - c.y);
      if (dd > rad + 4) continue;
      const n = (vnoise(x * 0.35, y * 0.35) - 0.5) * 1.6;
      plaza[k] = Math.max(plaza[k]!, smooth(rad + n, rad - 2.5 + n, dd));
    }
  }
  if (theme.id === 'town') {
    for (let k = 0; k < N; k++) {
      const e = sdf[k]!;
      if (e > 0.5) continue;
      const x = x0 + (k % nx) * CELL;
      const y = y0 + Math.floor(k / nx) * CELL;
      const n = (vnoise(x * 0.35, y * 0.35) - 0.5) * 1.2;
      plaza[k] = smooth(-0.8 + n, -3.2 + n, e);
    }
  }

  // ---- placements ------------------------------------------------------------------------
  const placements: OutPlacement[] = [];
  const lights: OutLight[] = [];
  const far: FarTree[] = [];
  const solids: { pos: Pt; r: number }[] = [];
  const groundAt = (p: Pt): number => sampleGrid(grid, height, p.x, p.y);
  const solidHash = new SpatialHash<{ pos: Pt; r: number }>(8);
  let solidMaxR = 0;
  const clearOf = (p: Pt, r: number): boolean => {
    const R = r + solidMaxR;
    for (const s of solidHash.near(p.x, p.y, R)) if (Math.hypot(s.pos.x - p.x, s.pos.y - p.y) < s.r + r) return false;
    return true;
  };
  // +valley: the theme can swap procs for its own look (tree_round → spruce, ...).
  const remapped = (id: string): string => {
    const alt = theme.remap?.[id];
    return alt && alt.length ? alt[Math.floor(rnd() * alt.length)]! : id;
  };
  const variants = (it: ScatterItem): string => `${remapped(it.id)}#${Math.floor(rnd() * (it.variants ?? 3))}`;
  const place = (it: ScatterItem, p: Pt, yaw = rnd() * Math.PI * 2, scaleK = 1, solid = !it.detail): OutPlacement => {
    const sc = (it.scale[0] + rnd() * (it.scale[1] - it.scale[0])) * scaleK;
    const id = variants(it);
    const pl: OutPlacement = {
      id,
      pos: p,
      y: groundAt(p) - (it.detail ? 0 : 0.05) - (it.sink ?? 0) * sc,
      yaw,
      scale: sc,
      tint: lerpTint(it.tint, rnd()),
      layer: layerOf(id, !!it.detail),
      r: it.r * sc,
      h: it.h * sc,
      castShadow: !it.detail && pl0Shadow(id),
    };
    placements.push(pl);
    if (solid) {
      const so = { pos: p, r: pl.r * 0.8 };
      solids.push(so);
      solidHash.addBox(p.x, p.y, p.x, p.y, so);
      solidMaxR = Math.max(solidMaxR, so.r);
    }
    return pl;
  };
  const addLight = (pl: OutPlacement, l: { kind: OutLightKind; dx: number; dz: number; y: number }): void => {
    // Local (dx, dz) → sim: local +x is the right of the facing, +z is the facing.
    const s = Math.sin(pl.yaw);
    const c = Math.cos(pl.yaw);
    // three: x = lx·c + lz·s, z = -lx·s + lz·c; sim y = -z.
    const tx = l.dx * pl.scale * c + l.dz * pl.scale * s;
    const tz = -l.dx * pl.scale * s + l.dz * pl.scale * c;
    lights.push({ pos: { x: pl.pos.x + tx, y: pl.pos.y - tz }, y: l.y * pl.scale, kind: l.kind });
  };

  // Authored hints first (they win the space).
  const hints: PropHint[] = d.props ?? [];
  const obstacleOf = inObstacle;
  // +zone: which obstacles hold an authored hint (they keep their dressing; no tree fill).
  const hinted = new Set<Poly>();
  const mounded = new Set<Poly>();
  for (const h of hints) {
    const r = hintRule(h.kind);
    if (!r) continue;
    const o = inObstacle(h.pos);
    if (!o) continue;
    if (!r.obstacleFill && !r.skip) hinted.add(o);
    if (r.skip && h.kind.toLowerCase().includes('mound')) mounded.add(o);
  }
  for (const h of hints) {
    const rule = hintRule(h.kind);
    if (!rule || rule.skip || rule.obstacleFill) continue;
    const ids = rule.ids(theme);
    if (!ids.length) continue;
    const facingYaw = h.facing !== undefined ? yawToward(Math.cos(h.facing), Math.sin(h.facing)) : rnd() * Math.PI * 2;
    if (rule.area) {
      // Flat scatter inside a radius (flower beds, mushroom rings): walk-through detail.
      const R = Math.max(1, h.scale ?? 2);
      if (rule.area === 'rows') {
        // +detail: a field of crop rows (3 yd pieces) along the hint's facing.
        const f = h.facing ?? rnd() * Math.PI;
        const ux = Math.cos(f);
        const uy = Math.sin(f);
        for (let a = -R; a <= R; a += 1.4)
          for (let b2 = -R; b2 <= R; b2 += 3.1) {
            const p = { x: h.pos.x + ux * b2 - uy * a, y: h.pos.y + uy * b2 + ux * a };
            if (Math.hypot(a, b2) > R || inObstacle(p)) continue;
            place({ id: ids[0]!, w: 1, scale: [0.9, 1.1], r: rule.r, h: rule.h, detail: true }, p, yawToward(ux, uy) + Math.PI / 2 + (rnd() - 0.5) * 0.06);
          }
        continue;
      }
      const n = rule.area === 'mushrooms' ? Math.round(R * 3) : rule.area === 'reeds' ? Math.round(3 + R * 3) : Math.round(R * R * (h.kind === 'wildflowers' ? 0.7 : 0.45));
      for (let i = 0; i < n; i++) {
        const a = rule.area === 'mushrooms' ? (i / n) * Math.PI * 2 : rnd() * Math.PI * 2;
        const r = rule.area === 'mushrooms' ? R * (0.85 + rnd() * 0.15) : R * Math.sqrt(rnd());
        const p = { x: h.pos.x + Math.cos(a) * r, y: h.pos.y + Math.sin(a) * r };
        // +detail: wildflower patches keep to one or two colours (drifts, not confetti).
        const pickId = h.kind === 'wildflowers' ? ids[(Math.floor(hash2(h.pos.x, h.pos.y) * ids.length) + (rnd() < 0.25 ? 1 : 0)) % ids.length]! : ids[Math.floor(rnd() * ids.length)]!;
        place({ id: pickId, w: 1, scale: [0.9, 1.3], r: rule.r, h: rule.h, detail: true }, p);
      }
      continue;
    }
    if (rule.row) {
      // Fences / palisades: `scale` sets the length (segments along the hint's local x).
      const n = Math.max(1, Math.round((h.scale ?? 1) * 2));
      const fx = Math.cos(h.facing ?? 0);
      const fy = Math.sin(h.facing ?? 0);
      for (let i = 0; i < n; i++) {
        const off = (i - (n - 1) / 2) * rule.row;
        const p = { x: h.pos.x + fy * off, y: h.pos.y - fx * off };
        place({ id: ids[0]!, w: 1, scale: [0.97, 1.03], r: rule.r, h: rule.h }, p, facingYaw, 1, false);
      }
      continue;
    }
    // Blockers (a hint on an obstacle): its scale is the obstacle radius, fit the model to it.
    const onObstacle = !!obstacleOf(h.pos);
    let sc = (h.scale ?? 1) * (rule.base ?? 1);
    if (onObstacle && h.scale !== undefined) sc = Math.min(1.35, Math.max(0.8, h.scale / rule.r)) * (rule.base ?? 1);
    const n = rule.clump ?? 1;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rnd();
      const off = n > 1 && i > 0 ? 1.5 + rnd() * 1.5 : 0;
      const p = { x: h.pos.x + Math.cos(a) * off, y: h.pos.y + Math.sin(a) * off };
      const id = ids[Math.floor(rnd() * ids.length)]!;
      const yaw = n === 1 && h.facing !== undefined ? facingYaw : rnd() * Math.PI * 2;
      const pl = place({ id, w: 1, scale: [sc * 0.94, sc * 1.06], r: rule.r, h: rule.h, ...(rule.detail ? { detail: true } : {}), variants: /^k[dhn]_/.test(id) ? 1 : 3 }, p, yaw);
      if (/^k[dhn]_/.test(id)) pl.id = id; // kit models have no variants
      if (rule.light) addLight(pl, rule.light);
    }
  }

  // Portal frames: a stone ring, a timber gate, or the barrow mound with its standing stones.
  for (const p of portals) {
    const fx = Math.cos(p.facing);
    const fy = Math.sin(p.facing);
    const at = (lx: number, lz: number): Pt => ({ x: p.pos.x + fy * lx + fx * lz, y: p.pos.y - fx * lx + fy * lz });
    const yaw = yawToward(fx, fy);
    const k = Math.max(1, p.radius / 1.6);
    const S = (id: string, r: number, h: number, sc: readonly [number, number] = [1, 1]): ScatterItem => ({ id, w: 1, scale: sc, r, h, variants: 2 });
    if (p.kind === 'portal') place(S('portal_ring', 0.6, 3.2, [k, k]), p.pos, yaw, 1, false);
    else if (p.kind === 'gate') place(S('gate_arch', 0.5, 5.6, [k, k]), p.pos, yaw, 1, false);
    else if (theme.barrowRuin) barrowRuin(p, at, yaw, S);
    else {
      place(S('barrow_mound', 7, 4.4), at(0, -3), yaw, 1, false);
      // A broken ring of standing stones around the mound (open toward the mouth).
      for (let i = 0; i < 7; i++) {
        const a = Math.PI * 0.32 + (i / 6) * Math.PI * 1.36;
        const r = 10.2 + (rnd() - 0.5) * 1.2;
        const q = at(Math.cos(a + Math.PI / 2) * r, -6 + Math.sin(a + Math.PI / 2) * r * 0.9);
        if (distAt(q.x, q.y) < 0.5) continue;
        place(S('standing_stone', 0.6, 3.6, [0.85, 1.15]), q, rnd() * Math.PI * 2, 1, false);
      }
      for (const [lx, lz, sc] of [
        [-4.1, -0.6, 1.0],
        [4.3, -1.2, 0.9],
        [-6.4, -4.2, 0.8],
        [6.0, -5.0, 0.85],
      ] as const) {
        const pl = place(S('crystals', 0.7, 2.0, [sc, sc]), at(lx, lz), rnd() * Math.PI * 2, 1, false);
        lights.push({ pos: pl.pos, y: 1.1, kind: 'crystal' });
      }
      // Ruined wall stubs flanking the approach.
      place(S('ruin_wall', 1.2, 2.6), at(-7.5, 1.5), yaw + 0.5, 1, false);
      place(S('ruin_wall', 1.2, 2.6), at(7.8, 0.6), yaw - 0.4, 1, false);
    }
  }

  // Obstacles: clumps of trees on big ones, boulders / single trees on small ones.
  for (let oi = 0; oi < obstacles.length; oi++) {
    const o = obstacles[oi]!;
    // Authored dressing already covers it (a well, a tent...), unless the hint asks for a fill.
    if (hinted.has(o)) continue;
    if (mounded.has(o)) continue; // the barrow mound
    const bb = bbox(o);
    const w = bb.x1 - bb.x0;
    const hgt = bb.y1 - bb.y0;
    const c = centroid(o);
    const small = Math.min(w, hgt) < 3.4;
    const lk = look(c.x, c.y);
    const rockFirst = hash2(c.x, c.y) < lk.rockChance;
    if (small) {
      const it = rockFirst || Math.min(w, hgt) < 2 ? pick(lk.obstacleRocks, rnd) : pick(lk.obstacleTrees, rnd);
      const k = Math.max(0.6, Math.min(1.4, Math.min(w, hgt) / (it.r * 2)));
      place(it, c, rnd() * Math.PI * 2, k);
      continue;
    }
    const step = 3.1;
    let any = false;
    for (let x = bb.x0 + step * 0.5; x < bb.x1; x += step)
      for (let y = bb.y0 + step * 0.5; y < bb.y1; y += step) {
        const p = { x: x + (rnd() - 0.5) * step * 0.6, y: y + (rnd() - 0.5) * step * 0.6 };
        if (!pointInPoly(p, o)) continue;
        any = true;
        const it = rockFirst && rnd() < 0.5 ? pick(lk.obstacleRocks, rnd) : pick(lk.obstacleTrees, rnd);
        place(it, p);
      }
    if (!any) place(pick(lk.obstacleTrees, rnd), c);
    // A ring of bushes / rocks around the clump's rim (inside the obstacle).
    for (let i = 0; i < o.length; i++) {
      const a = o[i]!;
      const bnext = o[(i + 1) % o.length]!;
      const len = Math.hypot(bnext.x - a.x, bnext.y - a.y);
      for (let t = 0.5; t < len; t += 2.4) {
        const p = { x: a.x + ((bnext.x - a.x) * t) / len, y: a.y + ((bnext.y - a.y) * t) / len };
        const q = { x: p.x + (c.x - p.x) * 0.12, y: p.y + (c.y - p.y) * 0.12 };
        if (rnd() < 0.6) place(pick(lk.edgeUnder, rnd), q, rnd() * Math.PI * 2, 0.9, false);
      }
    }
  }

  // Town: a well in the square, cottages around it, stalls and lanterns along the edge.
  if (theme.id === 'town') townLayout();

  // +valley: irregular flagstones along the paths (flat, walk-over).
  if (theme.flagstones) flagstones();

  // Edge line just outside the walkable edge.
  edgeLine();

  // +valley: big rock formations (valley walls) before the trees, so the trees fill around them.
  if (theme.cliffs) cliffBand(theme.cliffs);

  // Dense edge band (full trees + under-storey).
  {
    const s = theme.edgeSpacing;
    for (let y = y0; y < y0 + ny * CELL; y += s)
      for (let x = x0; x < x0 + nx * CELL; x += s) {
        const p = { x: x + (rnd() - 0.5) * s * 0.8, y: y + (rnd() - 0.5) * s * 0.8 };
        const e = distAt(p.x, p.y);
        // +zone: the tree wall's depth wanders (thin in places, deep in others).
        const band = theme.biomes ? theme.edgeBand * (0.45 + 1.15 * vnoise(p.x * 0.022 + 7, p.y * 0.022 - 3)) : theme.edgeBand;
        if (e < 2.6 || e > band) continue;
        if (isReserved(p, 1.5)) continue;
        if (rnd() > (e < 4.5 ? 0.7 : 0.94)) continue;
        if (inVista(p) && rnd() < 0.93) continue;
        if (edgeFrame && rnd() < mountainK(p.x, p.y) * 0.9) continue; // +edge: mountain stretches are bare rock
        const it = pick(look(p.x, p.y).edgeTrees, rnd);
        if (!clearOf(p, it.r * 0.7)) continue;
        place(it, p, rnd() * Math.PI * 2, 1 + Math.min(0.25, (e - 3) * 0.015));
      }
    const u = 3.0;
    for (let y = y0; y < y0 + ny * CELL; y += u)
      for (let x = x0; x < x0 + nx * CELL; x += u) {
        const p = { x: x + (rnd() - 0.5) * u * 0.9, y: y + (rnd() - 0.5) * u * 0.9 };
        const e = distAt(p.x, p.y);
        if (e < 1.8 || e > theme.edgeBand * 0.7) continue;
        if (isReserved(p, 0.5) || rnd() > 0.5) continue;
        if (edgeFrame && rnd() < mountainK(p.x, p.y) * 0.75) continue; // +edge
        const it = pick(look(p.x, p.y).edgeUnder, rnd);
        if (!it.detail && !clearOf(p, it.r * 0.6)) continue;
        place(it, p, rnd() * Math.PI * 2, 1, false);
      }
  }

  // Far ring: cheap instanced trees out to the margin.
  {
    const s = 6.2;
    for (let y = y0; y < y0 + ny * CELL; y += s)
      for (let x = x0; x < x0 + nx * CELL; x += s) {
        const p = { x: x + (rnd() - 0.5) * s * 0.9, y: y + (rnd() - 0.5) * s * 0.9 };
        const e = distAt(p.x, p.y);
        if (e < theme.edgeBand - 1 || isReserved(p, 2)) continue;
        if (rnd() < 0.08) continue;
        // +edge: trees thin out up the mountain flanks (a few hang on low down).
        if (edgeFrame && rnd() < mountainK(p.x, p.y) * (0.75 + 0.25 * smooth(30, 70, e))) continue;
        // +zone: clearings and bare ridges break the far forest (no uniform ring).
        if (theme.biomes && vnoise(p.x * 0.021 + 11, p.y * 0.021 - 5) < 0.33 && rnd() < 0.9) continue;
        const kind = theme.farTree.kind;
        // +detail: broadleaf biomes mix round crowns into the far ring.
        const pine = kind === 'pine' || (kind === 'spruce' && rnd() >= look(p.x, p.y).farRound) || (kind === 'mixed' && rnd() < 0.55);
        // Conifers vary a lot in height (spiky, layered tree lines); thinner in the Tower notch.
        if (kind === 'spruce' && inVista(p) && rnd() < 0.85) continue;
        const sc = kind === 'spruce' ? 0.7 + rnd() * rnd() * 1.3 : 0.9 + rnd() * 0.5;
        const ft = look(p.x, p.y).farTint;
        far.push({ pos: p, scale: sc + Math.min(0.4, (e - theme.edgeBand) * 0.008), yaw: rnd() * Math.PI * 2, pine, ...(ft[0] !== 1 || ft[1] !== 1 || ft[2] !== 1 ? { tint: ft } : {}) });
      }
  }

  // Walk-through decor on the walkable ground (off paths, plazas and portals).
  {
    let area = 0;
    for (const p of walk) area += polyArea(p);
    // +zone: biomes vary the density: throw for the densest, keep each by its biome's share.
    const maxD = Math.max(theme.decorDensity, ...Object.values(theme.biomes ?? {}).map((b) => b.decorDensity ?? 0));
    const n = Math.round((area / 100) * maxD);
    const bb = bbox(walk.flat());
    for (let i = 0, tries = 0; i < n && tries < n * 6; tries++) {
      const p = { x: bb.x0 + rnd() * (bb.x1 - bb.x0), y: bb.y0 + rnd() * (bb.y1 - bb.y0) };
      const e = distAt(p.x, p.y);
      if (e > -0.6) continue;
      if (sampleGrid(grid, pathW, p.x, p.y) > 0.2 || sampleGrid(grid, plaza, p.x, p.y) > 0.3) continue;
      if (isReserved(p, 0.5) || inObstacle(p)) continue;
      // Clustered: decor likes the quieter edges of the ground.
      if (vnoise(p.x * 0.08 + 40, p.y * 0.08) < 0.45 - Math.min(0.3, -e * 0.0) && rnd() < 0.6) continue;
      const lk = look(p.x, p.y);
      if (rnd() > lk.decorDensity / maxD) {
        i++;
        continue;
      }
      // +detail: decor drifts: neighbours mostly share a kind (patches of one colour, not confetti).
      place(rnd() < 0.7 ? pickAt(lk.decor, p.x, p.y) : pick(lk.decor, rnd), p, rnd() * Math.PI * 2, 1, false);
      i++;
    }
  }

  // +detail: open-ground scatter, road verges and the lake shore (biome-appropriate; walk-through).
  scatterPass();
  vergePass();
  shorePass();

  // Shade (under the edge trees and obstacle canopies): painted ambient occlusion on the ground.
  const shade = new Float32Array(N);
  for (const pl of placements) {
    if (pl.layer !== 'prop' || pl.h < 4) continue;
    const R = pl.r * 1.6 + 1.2;
    const i0 = Math.max(0, Math.floor((pl.pos.x - R - x0) / CELL));
    const i1 = Math.min(nx - 1, Math.ceil((pl.pos.x + R - x0) / CELL));
    const j0 = Math.max(0, Math.floor((pl.pos.y - R - y0) / CELL));
    const j1 = Math.min(ny - 1, Math.ceil((pl.pos.y + R - y0) / CELL));
    for (let j = j0; j <= j1; j++)
      for (let i = i0; i <= i1; i++) {
        const dd = Math.hypot(x0 + i * CELL - pl.pos.x, y0 + j * CELL - pl.pos.y);
        if (dd >= R) continue;
        const k = j * nx + i;
        shade[k] = Math.max(shade[k]!, 0.75 * (1 - dd / R) ** 1.5);
      }
  }

  const probe = { x: 0, y: 0 };
  const blocked = (x: number, y: number): boolean => {
    probe.x = x;
    probe.y = y;
    return !!inObstacle(probe);
  };
  return { theme, walk, obstacles, grid, sdf, height, path: pathW, plaza, shade, placements, far, paths, lights, reserved, hub, look, terrain: !!terrain, blocked };

  // ---- +detail ---------------------------------------------------------------------------
  /** A weighted pick driven by low-frequency noise at (x, y): neighbours agree (drifts of one kind). */
  function pickAt<T extends { w: number }>(items: readonly T[], x: number, y: number): T {
    const u = (vnoise(x * 0.06 + 17, y * 0.06 - 9) * 4.3 + vnoise(x * 0.013 - 5, y * 0.013 + 2) * 1.7) % 1;
    let total = 0;
    for (const it of items) total += it.w;
    let r = u * total;
    for (const it of items) {
      r -= it.w;
      if (r <= 0) return it;
    }
    return items[items.length - 1]!;
  }

  /** Open ground on the walkable side, off paths, plazas, water, portals and obstacles. */
  function openGround(p: Pt, pad: number): boolean {
    if (distAt(p.x, p.y) > -1) return false;
    if (sampleGrid(grid, pathW, p.x, p.y) > 0.08 || sampleGrid(grid, plaza, p.x, p.y) > 0.05) return false;
    if (groundAt(p) < -0.1 || isReserved(p, pad) || inObstacle(p)) return false;
    return true;
  }

  /** Bushes, boulders, logs and stumps over the open ground, gathered in loose groups (biome densities). */
  function scatterPass(): void {
    const maxD = Math.max(0, ...[theme.id, ...Object.keys(theme.biomes ?? {})].map((id) => resolveLook(theme, id).scatterDensity));
    if (maxD <= 0) return;
    let area = 0;
    for (const p of walk) area += polyArea(p);
    const n = Math.round((area / 100) * maxD);
    const bb = bbox(walk.flat());
    for (let i = 0; i < n; i++) {
      const p = { x: bb.x0 + rnd() * (bb.x1 - bb.x0), y: bb.y0 + rnd() * (bb.y1 - bb.y0) };
      const lk = look(p.x, p.y);
      if (!lk.scatter.length || rnd() > lk.scatterDensity / maxD) continue;
      // Groups: dense where the grouping noise is high, sparse elsewhere.
      const g = vnoise(p.x * 0.035 + 61, p.y * 0.035 - 23);
      if (rnd() > 0.15 + 1.5 * Math.max(0, g - 0.42)) continue;
      const it = pickAt(lk.scatter, p.x + 40, p.y - 40);
      if (!openGround(p, 1.5) || !clearOf(p, it.r * 0.6)) continue;
      const pl = place(it, p, rnd() * Math.PI * 2, 1, false);
      pl.castShadow = !it.detail;
    }
  }

  /** Road verges: flowers, long grass and pebbles just off the flagstones, a bush or a rock now and then. */
  function vergePass(): void {
    for (const pa of paths) {
      for (let k = 0; k < pa.pts.length - 1; k++) {
        const a = pa.pts[k]!;
        const b = pa.pts[k + 1]!;
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < 0.5) continue;
        const ux = (b.x - a.x) / len;
        const uy = (b.y - a.y) / len;
        for (const side of [-1, 1]) {
          if (rnd() < 0.35) continue;
          const t = rnd() * len;
          const lat = (pa.width * 0.5 + 0.5 + rnd() * rnd() * 2.6) * side;
          const p = { x: a.x + ux * t - uy * lat, y: a.y + uy * t + ux * lat };
          const lk = look(p.x, p.y);
          if (!lk.verge.length) continue;
          if (distAt(p.x, p.y) > -1 || sampleGrid(grid, pathW, p.x, p.y) > 0.45 || sampleGrid(grid, plaza, p.x, p.y) > 0.05) continue;
          if (groundAt(p) < -0.1 || isReserved(p, 0.6) || inObstacle(p)) continue;
          const it = rnd() < 0.6 ? pickAt(lk.verge, p.x, p.y) : pick(lk.verge, rnd);
          if (!it.detail && !clearOf(p, it.r * 0.5)) continue;
          place(it, p, rnd() * Math.PI * 2, 1, false);
        }
      }
    }
  }

  /** Lily pads on the lake's shallows, tall reeds and cattails along its edge (+zone terrain water). */
  function shorePass(): void {
    const w = terrain?.water;
    if (!w) return;
    const lv = w.level;
    const { c, rx, ry } = w.lake;
    const step = 2.4;
    for (let y = c.y - ry * 1.45; y < c.y + ry * 1.45; y += step)
      for (let x = c.x - rx * 1.45; x < c.x + rx * 1.45; x += step) {
        const p = { x: x + (rnd() - 0.5) * step, y: y + (rnd() - 0.5) * step };
        const h = groundAt(p);
        if (isReserved(p, 1)) continue;
        if (h < lv - 0.18 && h > lv - 1.1) {
          if (rnd() > 0.16 * (0.4 + vnoise(p.x * 0.08, p.y * 0.08) * 1.4)) continue;
          const pl = place({ id: 'reeds_lily', w: 1, scale: [0.8, 1.3], r: 1.2, h: 0.1, detail: true }, p, rnd() * Math.PI * 2, 1, false);
          pl.y = lv + 0.015;
        } else if (h > lv - 0.3 && h < lv + 0.3) {
          if (rnd() > 0.15 * (0.3 + vnoise(p.x * 0.07 + 4, p.y * 0.07) * 1.6) || inObstacle(p)) continue;
          place({ id: rnd() < 0.45 ? 'reeds_tall' : 'reeds', w: 1, scale: [0.85, 1.3], r: 0.6, h: 1.8, detail: true }, p, rnd() * Math.PI * 2, 1, false);
        }
      }
  }

  // ---- +valley ---------------------------------------------------------------------------
  /** Big rocks in the band beyond the edge, never overhanging the walkable ground. */
  function cliffBand(c: NonNullable<OutdoorTheme['cliffs']>): void {
    const s = c.spacing;
    for (let y = y0; y < y0 + ny * CELL; y += s)
      for (let x = x0; x < x0 + nx * CELL; x += s) {
        const p = { x: x + (rnd() - 0.5) * s * 0.9, y: y + (rnd() - 0.5) * s * 0.9 };
        const e = distAt(p.x, p.y);
        // +edge: on the zone the crags climb the whole mountain flank (and stay rare on the forested stretches).
        const mk = edgeFrame ? mountainK(p.x, p.y) : 0;
        if (e < c.band[0] || e > (edgeFrame ? c.band[1] + 60 * mk : c.band[1]) || isReserved(p, 3)) continue;
        if (edgeFrame && rnd() > 0.25 + 0.75 * mk) continue;
        // Denser near the edge (the valley walls), sparse outcrops further up the hills.
        // +zone: outcrops and ridgelines: crags gather where the ridge noise runs high.
        const ridge = theme.biomes ? 0.5 + 1.5 * smooth(0.45, 0.72, vnoise(p.x * 0.03 + 3, p.y * 0.03 + 17)) : 1;
        if (rnd() > c.density * look(p.x, p.y).cliffK * ridge * (e < 16 ? 1 : 0.55)) continue;
        if (inVista(p) && e < 30) continue;
        const it = pick(c.items, rnd);
        // Bigger further out (the walls rise with the hills).
        const k = 1 + Math.min(edgeFrame ? 0.9 : 0.35, Math.max(0, e - 10) * (edgeFrame ? 0.02 : 0.012));
        const rMax = it.r * it.scale[1] * k;
        if (e < rMax + 1.5 || !clearOf(p, it.r * 0.6)) continue;
        place(it, p, rnd() * Math.PI * 2, k);
      }
  }

  /** Flagstones along every path: dart-thrown, bigger in the middle, gaps of worn grass. */
  function flagstones(): void {
    const cellK = 1.0;
    const grid = new Map<string, { x: number; y: number; r: number }[]>();
    const keyOf = (x: number, y: number): string => `${Math.floor(x / cellK)}|${Math.floor(y / cellK)}`;
    const free = (x: number, y: number, r: number): boolean => {
      const cx = Math.floor(x / cellK);
      const cy = Math.floor(y / cellK);
      for (let i = cx - 2; i <= cx + 2; i++)
        for (let j = cy - 2; j <= cy + 2; j++)
          for (const q of grid.get(`${i}|${j}`) ?? []) if (Math.hypot(q.x - x, q.y - y) < (q.r + r) * 0.86) return false;
      return true;
    };
    let n = 0;
    for (const pa of paths) {
      if (pa.trail) continue;
      for (let k = 0; k < pa.pts.length - 1; k++) {
        const a = pa.pts[k]!;
        const b = pa.pts[k + 1]!;
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < 0.1) continue;
        const ux = (b.x - a.x) / len;
        const uy = (b.y - a.y) / len;
        for (let t = 0; t < len; t += 0.3) {
          for (let tries = 0; tries < 2; tries++) {
            const lat = (rnd() - 0.5) * pa.width * 0.8;
            const edge = Math.abs(lat) / (pa.width * 0.4);
            const x = a.x + ux * t - uy * lat;
            const y = a.y + uy * t + ux * lat;
            if (distAt(x, y) > 1.5) continue;
            // Worn gaps: noise patches and the ragged edges show grass between the stones.
            if (vnoise(x * 0.35 + 7, y * 0.35) < 0.2 + edge * 0.25 || rnd() < edge * 0.45) continue;
            const sc = (0.95 - edge * 0.35) * (0.8 + rnd() * 0.35);
            const r = 0.48 * sc;
            if (!free(x, y, r)) continue;
            const kk = keyOf(x, y);
            const l = grid.get(kk);
            if (l) l.push({ x, y, r });
            else grid.set(kk, [{ x, y, r }]);
            placements.push({ id: `flagstone#${n % 7}`, pos: { x, y }, y: groundAt({ x, y }) + (n % 3) * 0.006, yaw: rnd() * Math.PI * 2, scale: sc, tint: [1, 1, 1], layer: 'ground', r, h: 0.1, castShadow: false });
            n++;
          }
        }
      }
    }
  }

  /**
   * The barrow as a ruin: a mossy rock outcrop with a pale arch over the mouth, the crystal
   * monument rising from the outcrop, crystal clusters at its foot (the obstacle covers the outcrop).
   */
  function barrowRuin(p: PortalDef, at: (lx: number, lz: number) => Pt, yaw: number, S: (id: string, r: number, h: number, sc?: readonly [number, number]) => ScatterItem): void {
    place(S('barrow_mound', 7, 4.4), at(0, -3), yaw, 1, false);
    const mono = place(S('glow_monument', 1.2, 7, [1.3, 1.3]), at(0.3, -6.8), rnd() * Math.PI * 2, 1, false);
    mono.y += 3.4;
    lights.push({ pos: mono.pos, y: mono.y - groundAt(mono.pos) + 5.2, kind: 'monolith' });
    for (const [lx, lz, sc] of [
      [-5.6, -2.8, 1.0],
      [5.4, -3.4, 0.9],
      [-3.4, -9.6, 1.2],
      [4.2, -9.8, 1.1],
      [-6.6, -6.0, 0.8],
    ] as const) {
      const q = at(lx, lz);
      const pl = place(S('crystals', 0.7, 2.0, [sc, sc]), q, rnd() * Math.PI * 2, 1, false);
      pl.y = groundAt(q) + (Math.abs(lz) > 5 ? 1.2 : 0.1);
      lights.push({ pos: pl.pos, y: pl.y - groundAt(q) + 1.0, kind: 'crystal' });
    }
    void p;
  }

  // ---- town ---------------------------------------------------------------------------
  function townLayout(): void {
    const S = (id: string, r: number, h: number): ScatterItem => ({ id, w: 1, scale: [0.95, 1.05], r, h });
    const hasHint = (re: RegExp): boolean => hints.some((h) => re.test(h.kind.toLowerCase()));
    if (!hasHint(/well/) && !isReserved(hub, 1.5)) place(S('well', 1.3, 3.7), hub, 0.3);
    // Lanterns ring the square (inside the walkable ground, walk-through).
    if (!hasHint(/lantern|lamp/)) {
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + 0.4;
        const p = { x: hub.x + Math.cos(a) * 8.5, y: hub.y + Math.sin(a) * 8.5 };
        if (distAt(p.x, p.y) > -1 || isReserved(p, 0.5)) continue;
        const pl = place(S('lantern_post', 0.3, 3.1), p, yawToward(Math.cos(a + Math.PI / 2), Math.sin(a + Math.PI / 2)));
        addLight(pl, { kind: 'lantern', dx: 0.75, dz: 0, y: 2.45 });
      }
    }
    // Walk the walkable boundary: stalls just outside, cottages behind, lanterns at the corners
    // (only when the map doesn't author its own house ring).
    const boundary = hasHint(/house|cottage/) ? [] : boundaryRuns();
    let cottageAt = 4;
    let stallAt = 6;
    let lampAt = 2;
    let travelled = 0;
    const cottages = ['cottage_red', 'cottage_slate', 'cottage_thatch'];
    for (const run of boundary) {
      const { a, b: c, out } = run;
      const len = Math.hypot(c.x - a.x, c.y - a.y);
      for (let t = 0; t < len; t += 0.5) {
        travelled += 0.5;
        const p = { x: a.x + ((c.x - a.x) * t) / len, y: a.y + ((c.y - a.y) * t) / len };
        const inward = yawToward(-out.x, -out.y);
        if (travelled >= cottageAt) {
          const q = { x: p.x + out.x * 8.2, y: p.y + out.y * 8.2 };
          if (!isReserved(q, 4.5) && clearOf(q, 3.4) && distAt(q.x, q.y) > 5.5) {
            place(S(cottages[Math.floor(rnd() * 3)]!, 3.6, 7), q, inward + (rnd() - 0.5) * 0.12);
            cottageAt = travelled + 11 + rnd() * 3;
          } else cottageAt = travelled + 2;
        }
        if (travelled >= stallAt) {
          const q = { x: p.x + out.x * 2.6, y: p.y + out.y * 2.6 };
          if (!isReserved(q, 2.5) && clearOf(q, 1.8)) {
            place(S(rnd() < 0.5 ? 'stall_red' : 'stall_blue', 1.8, 3), q, inward);
            stallAt = travelled + 15 + rnd() * 6;
          } else stallAt = travelled + 2;
        }
        if (travelled >= lampAt) {
          const q = { x: p.x + out.x * 0.9, y: p.y + out.y * 0.9 };
          if (!isReserved(q, 0.8) && clearOf(q, 0.6)) {
            const pl = place(S('lantern_post', 0.3, 3.1), q, inward + Math.PI / 2);
            addLight(pl, { kind: 'lantern', dx: 0.75, dz: 0, y: 2.45 });
            lampAt = travelled + 13;
          } else lampAt = travelled + 1;
        }
      }
    }
    // Banners at the portals / gates (both sides of each mouth).
    for (const p of hasHint(/banner/) ? [] : portals) {
      if (p.kind === 'dungeon') continue;
      const fx = Math.cos(p.facing);
      const fy = Math.sin(p.facing);
      for (const s of [-1, 1]) {
        const q = { x: p.pos.x - fy * s * (p.radius + 3.2) + fx * 1.0, y: p.pos.y + fx * s * (p.radius + 3.2) + fy * 1.0 };
        place(S(rnd() < 0.5 ? 'banner_red' : 'banner_blue', 0.3, 5), q, yawToward(fx, fy));
      }
    }
  }

  // ---- edge line ------------------------------------------------------------------------
  function edgeLine(): void {
    const kinds = theme.edgeLine;
    let kind = kinds[0]!;
    let runLeft = 0;
    for (const run of boundaryRuns()) {
      const { a, b: c, out } = run;
      const len = Math.hypot(c.x - a.x, c.y - a.y);
      const dir = { x: (c.x - a.x) / len, y: (c.y - a.y) / len };
      const step = kind === 'fence' ? 3 : kind === 'hedge' ? 2.8 : kind === 'fern' ? 1.6 : 2.3;
      for (let t = step / 2; t < len; t += step) {
        if (runLeft <= 0) {
          kind = kinds[Math.floor(rnd() * kinds.length)]!;
          runLeft = 4 + Math.floor(rnd() * 6);
        }
        runLeft--;
        const p = { x: a.x + dir.x * t + out.x * 1.05, y: a.y + dir.y * t + out.y * 1.05 };
        if (isReserved(p, 0.8)) continue;
        if (distAt(p.x, p.y) < 0.6) continue; // a corner pocket: stays inside the ground
        const along = yawToward(dir.x, dir.y) - Math.PI / 2;
        if (kind === 'fence') place({ id: 'fence', w: 1, scale: [0.98, 1.02], r: 0.3, h: 1.25 }, p, along, 1, false);
        else if (kind === 'hedge') place({ id: 'hedge', w: 1, scale: [0.95, 1.08], r: 0.8, h: 1.4 }, p, along, 1, false);
        else if (kind === 'bramble') place({ id: 'bramble', w: 1, scale: [0.9, 1.3], r: 1.0, h: 1.2 }, { x: p.x + out.x * 0.4, y: p.y + out.y * 0.4 }, rnd() * 6.28, 1, false);
        else if (kind === 'bush') place({ id: theme.id === 'forest' ? 'bush_dark' : 'bush', w: 1, scale: [0.9, 1.35], r: 0.9, h: 1.3 }, { x: p.x + out.x * 0.5, y: p.y + out.y * 0.5 }, rnd() * 6.28, 1, false);
        else if (kind === 'mossrock') place({ id: rnd() < 0.3 ? 'mossrock_big' : 'mossrock', w: 1, scale: [0.6, 1.0], r: 1.0, h: 1.0, sink: 0.15 }, { x: p.x + out.x * 1.2, y: p.y + out.y * 1.2 }, rnd() * 6.28, 1, false);
        else if (kind === 'fern') place({ id: 'fern', w: 1, scale: [0.9, 1.4], r: 0.8, h: 0.7, detail: true }, { x: p.x + out.x * 0.2, y: p.y + out.y * 0.2 }, rnd() * 6.28, 1, false);
        else place({ id: rnd() < 0.3 ? 'rock_big' : 'rock', w: 1, scale: [0.8, 1.2], r: 1.0, h: 1.0 }, { x: p.x + out.x * 0.6, y: p.y + out.y * 0.6 }, rnd() * 6.28, 1, false);
      }
    }
  }

  /** Walkable edges that are on the union boundary (outside of them is not walkable). */
  function boundaryRuns(): { a: Pt; b: Pt; out: Pt }[] {
    const out: { a: Pt; b: Pt; out: Pt }[] = [];
    for (const poly of walk) {
      const ccwPoly = polySigned(poly) < 0 ? [...poly].reverse() : poly;
      for (let i = 0; i < ccwPoly.length; i++) {
        const a = ccwPoly[i]!;
        const c = ccwPoly[(i + 1) % ccwPoly.length]!;
        const len = Math.hypot(c.x - a.x, c.y - a.y);
        if (len < 0.5) continue;
        const n = { x: (c.y - a.y) / len, y: -(c.x - a.x) / len };
        // Split long edges where they run along another walkable polygon (corridor mouths).
        const N2 = Math.max(1, Math.ceil(len / 2));
        let start: Pt | null = null;
        for (let k = 0; k <= N2; k++) {
          const t = k / N2;
          const p = { x: a.x + (c.x - a.x) * t, y: a.y + (c.y - a.y) * t };
          const probe = { x: p.x + n.x * 0.8, y: p.y + n.y * 0.8 };
          const open = !inWalk(probe);
          if (open && !start) start = p;
          if ((!open || k === N2) && start) {
            const end = open ? p : { x: a.x + (c.x - a.x) * ((k - 0.5) / N2), y: a.y + (c.y - a.y) * ((k - 0.5) / N2) };
            if (Math.hypot(end.x - start.x, end.y - start.y) > 1) out.push({ a: start, b: end, out: n });
            start = null;
          }
        }
      }
    }
    return out;
  }
}

/** Layer for a proc id: unlit crystals, flat ground stones, walk-through detail, or a solid prop. */
function layerOf(id: string, detail: boolean): OutLayer {
  if (id.startsWith('glow_')) return 'glow';
  if (id.startsWith('flagstone')) return 'ground';
  return detail ? 'detail' : 'prop';
}
const pl0Shadow = (id: string): boolean => !id.startsWith('glow_');

function polySigned(p: readonly Pt[]): number {
  let s = 0;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) s += p[j]!.x * p[i]!.y - p[i]!.x * p[j]!.y;
  return s / 2;
}
const polyArea = (p: readonly Pt[]): number => Math.abs(polySigned(p));

/** A gently wandering polyline a → b (worn paths). */
function wigglePath(a: Vec2, b: Vec2, rnd: () => number): Pt[] {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const n = Math.max(2, Math.ceil(len / 4));
  const nx = -(b.y - a.y) / len;
  const ny = (b.x - a.x) / len;
  const seed = rnd() * 50;
  const pts: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const env = Math.sin(t * Math.PI);
    const off = (vnoise(seed + t * 3, seed) - 0.5) * 6 * env;
    pts.push({ x: a.x + (b.x - a.x) * t + nx * off, y: a.y + (b.y - a.y) * t + ny * off });
  }
  return pts;
}

/** +zone: a uniform-grid hash of items by bounding box (big maps: neighbour queries without full scans). */
class SpatialHash<T> {
  private readonly cells = new Map<number, T[]>();
  private static readonly EMPTY: readonly never[] = [];
  constructor(private readonly size: number) {}
  private key(ix: number, iy: number): number {
    return (ix + 4096) * 8192 + (iy + 4096);
  }
  addBox(x0: number, y0: number, x1: number, y1: number, item: T): void {
    const s = this.size;
    for (let ix = Math.floor(x0 / s); ix <= Math.floor(x1 / s); ix++)
      for (let iy = Math.floor(y0 / s); iy <= Math.floor(y1 / s); iy++) {
        const k = this.key(ix, iy);
        const l = this.cells.get(k);
        if (l) l.push(item);
        else this.cells.set(k, [item]);
      }
  }
  /** Items whose box covers the cell of (x, y). */
  at(x: number, y: number): readonly T[] {
    return this.cells.get(this.key(Math.floor(x / this.size), Math.floor(y / this.size))) ?? SpatialHash.EMPTY;
  }
  /** Items in the cells within `r` of (x, y) (point items added with a zero box). */
  *near(x: number, y: number, r: number): Generator<T> {
    const s = this.size;
    for (let ix = Math.floor((x - r) / s); ix <= Math.floor((x + r) / s); ix++)
      for (let iy = Math.floor((y - r) / s); iy <= Math.floor((y + r) / s); iy++) {
        const l = this.cells.get(this.key(ix, iy));
        if (l) yield* l;
      }
  }
}
